import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Alert, Spin } from 'antd';
import { api, getToken, setToken } from './api.js';

export type Account = {
  id: string;
  username: string;
  displayName: string;
  organizationId: string;
  isPlatformAdmin: boolean;
  capabilities: string[];
  bonusManager?: boolean;
  bonusSelfView?: boolean;
};

type AuthState = {
  account: Account | null;
  loading: boolean;
  login(username: string, password: string): Promise<void>;
  logout(): Promise<void>;
  can(capability: string): boolean;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(Boolean(getToken()));

  useEffect(() => {
    const refresh = () => {
      if (!getToken()) {
        setAccount(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      void api<Account>('/auth/me').then(setAccount).catch(() => setAccount(null)).finally(() => setLoading(false));
    };
    refresh();
    window.addEventListener('erp-auth-change', refresh);
    return () => window.removeEventListener('erp-auth-change', refresh);
  }, []);

  const value = useMemo<AuthState>(() => ({
    account,
    loading,
    async login(username, password) {
      const result = await api<{ token: string; account: Account }>('/auth/login', {
        method: 'POST', body: JSON.stringify({ username, password })
      });
      localStorage.setItem('after-sales-erp-token', result.token);
      setAccount(result.account);
      setLoading(false);
    },
    async logout() {
      try { await api('/auth/logout', { method: 'POST' }); } finally { setToken(null); setAccount(null); }
    },
    can(capability) { return capability === 'BONUS_MANAGE' ? Boolean(account?.bonusManager)
      : capability === 'BONUS_SELF_VIEW' ? Boolean(account?.bonusManager || account?.bonusSelfView)
      : Boolean(account?.isPlatformAdmin || account?.capabilities.includes(capability)); }
  }), [account, loading]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error('AuthProvider is missing');
  return value;
}

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const location = useLocation();
  if (auth.loading) return <div className="screen-center"><Spin size="large" /></div>;
  if (!auth.account) return <Navigate to="/login" replace state={{ returnTo: location.pathname + location.search }} />;
  return children;
}

export function RequireAnyCapability({ capabilities, children }: { capabilities: string[]; children: React.ReactNode }) {
  const auth = useAuth();
  if (auth.loading) return <div className="screen-center"><Spin size="large" /></div>;
  if (!capabilities.some((capability) => auth.can(capability))) {
    return <div className="route-state"><Alert type="error" showIcon title="无权访问" description="当前账号没有访问此页面的权限。" /></div>;
  }
  return children;
}

export function RequirePlatformAdmin({children}:{children:React.ReactNode}) {
  const auth=useAuth();
  if (auth.loading) return <div className="screen-center"><Spin size="large" /></div>;
  if (!auth.account?.isPlatformAdmin) return <div className="route-state"><Alert type="error" showIcon title="无权访问" description="仅平台管理员可管理地图服务密钥。" /></div>;
  return children;
}
